import { flatten } from 'lodash'
import { Component, OnInit, Input } from '@angular/core'
import { GALLERY_IMAGE } from 'ngx-image-gallery'
import { FileService } from 'src/app/core/file.service'
import {
  TestResult,
  TestResultSeries,
  TestOutputFile,
} from 'common/testing/ge_big'
import { DocxTestOutputFile, ImageTestOutputFile } from 'common/testing/manual'
import { SeriesGroup } from 'common/aqsTypes'

/** one gallery of a by-phantom layout (see `groups`) */
interface GroupGallery {
  title: string
  images: GALLERY_IMAGE[]
  dcmKeys: string[]
}

@Component({
  selector: 'app-series-viewer',
  templateUrl: './series-viewer.component.html',
  styleUrls: ['./series-viewer.component.scss'],
})
export class SeriesViewerComponent implements OnInit {
  @Input() result: TestResult
  /**
   * Optional grouping of the series (evaluationItems.seriesGroups, written by
   * evaluations that lay a test out by phantom): one gallery per group instead
   * of one per series. Series not listed in any group go to a trailing
   * "Other series" gallery. Absent for the series-list layouts.
   */
  @Input() groups: SeriesGroup[] | undefined
  testImages: GALLERY_IMAGE[]
  seriesImages: GALLERY_IMAGE[][]
  /**
   * Parallel to `seriesImages`: the stored `.dcm` key for each gallery image,
   * handed to `app-image-viewer` so its per-image "DICOM header" control can
   * fetch + parse the raw image on demand.
   */
  seriesDcmKeys: string[][]
  groupGalleries: GroupGallery[] | undefined
  documents: DocxTestOutputFile[]

  constructor(private fileService: FileService) {}

  ngOnInit(): void {
    const adapter = (file: TestOutputFile): GALLERY_IMAGE => ({
      title: file.label,
      url: this.src(file.location),
    })
    const imageFiles = (file: TestOutputFile): file is ImageTestOutputFile =>
      file.file_type === 'image'
    const docxFiles = (file: TestOutputFile): file is DocxTestOutputFile =>
      file.file_type === 'docx'
    const assembleGalleryImages = (series: TestResultSeries): GALLERY_IMAGE[] =>
      flatten(series.images.map((i) => i.annoFiles || [])).map(adapter)
    // Parallel to `assembleGalleryImages`: each tile's DICOM key comes from the
    // output file's own `origFile` — set by the analyzer on DICOM renders, absent
    // on generated charts (→ empty key → no "DICOM header" button). No fallback:
    // studies analyzed before `origFile` existed simply show no button.
    const assembleDcmKeys = (series: TestResultSeries): string[] =>
      flatten(series.images.map((i) => i.annoFiles || [])).map((f) =>
        this.dcmKeyFromOrigFile(f.origFile),
      )
    this.testImages = this.result.testFiles?.filter(imageFiles).map(adapter)
    this.documents = this.result.testFiles?.filter(docxFiles)
    this.seriesImages = this.result.series?.map(assembleGalleryImages)
    this.seriesDcmKeys = this.result.series?.map(assembleDcmKeys)
    this.groupGalleries = this.assembleGroupGalleries(assembleDcmKeys)
  }

  src(key: string): string {
    return this.fileService.getHostedUrl(key)
  }

  /**
   * One gallery per group, in group order, with the group's series in the
   * listed order; the tile title carries the exposure's intent so a
   * FOR PROCESSING / FOR PRESENTATION pair stays distinguishable inside one
   * gallery. Returns undefined when there is nothing to group by.
   */
  private assembleGroupGalleries(
    assembleDcmKeys: (series: TestResultSeries) => string[],
  ): GroupGallery[] | undefined {
    const series = this.result.series
    if (!this.groups || this.groups.length === 0 || !series) {
      return undefined
    }
    const groupedImages = (s: TestResultSeries): GALLERY_IMAGE[] => {
      const intent: unknown = (s.parameters as { [key: string]: unknown })
        ?.PresentationIntentType
      return flatten(s.images.map((i) => i.annoFiles || [])).map((file) => ({
        title: intent ? `${file.label} · ${intent}` : file.label,
        url: this.src(file.location),
      }))
    }
    const bySeriesUID = new Map<string, TestResultSeries>()
    series.forEach((s) => bySeriesUID.set(s.seriesUID, s))
    const listed = new Set<string>()
    const gallery = (
      title: string,
      members: TestResultSeries[],
    ): GroupGallery => ({
      title,
      images: flatten(members.map(groupedImages)),
      dcmKeys: flatten(members.map(assembleDcmKeys)),
    })
    const galleries = this.groups.map((group) => {
      const members: TestResultSeries[] = []
      group.seriesUIDs.forEach((uid) => {
        listed.add(uid)
        const member = bySeriesUID.get(uid)
        if (member) {
          members.push(member)
        }
      })
      return gallery(group.title, members)
    })
    const rest = series.filter((s) => !listed.has(s.seriesUID))
    if (rest.length > 0) {
      galleries.push(gallery('Other series', rest))
    }
    return galleries
  }

  /**
   * `origFile` is the analyzer's local path (e.g. `/tmp/Room 1__<uid>.dcm`);
   * its basename is the stored file key that the `/files` route serves.
   */
  private dcmKeyFromOrigFile(origFile: string | null | undefined): string {
    if (!origFile) return ''
    return origFile.split(/[\\/]/).pop() || ''
  }
}
